// src/pages/DashboardLayout.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useMotionValue, useDragControls } from 'framer-motion';
import BiQChatPopup from '../components/BiQChatPopup';
import TutorialModal from '../components/TutorialModal';
import LanguageSelector from '../components/LanguageSelector';
import SidebarUserMenu from '../components/SidebarUserMenu';
import SidebarChatHistory from '../components/SidebarChatHistory';
import CustomScrollbar from '../components/CustomScrollbar';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import { BiqChatHistoryProvider, useBiqChatHistory } from '../context/BiqChatHistoryContext';
import { BiqLiveChatsProvider } from '../context/BiqLiveChatsContext';
import { BiqPageContextProvider } from '../context/BiqPageContext';
import { trackEvent, EVENTS } from '../utils/analytics';
import { ROUTES } from '../constants/Routes';
import { ENABLE_PROCUREMENT_PLANNER } from '../constants/featureFlags';
import { GlobalProduceMap, NavBell, SideNav, SideNavExpand, SideNavCollapse } from '../components/icons';
import HoverTooltip from '../components/HoverTooltip';
import MobileMenuButton from '../components/MobileMenuButton';
import useViewportBounds from '../hooks/useViewportBounds';
import { MapParamsContext, useMapParamsState } from '../hooks/useMapParams';
import { useCommodities } from '../utils/api-hooks/commodity';

// Import icons
import {
  X,
  MessageSquare,
  User,
  ChevronDown,
  CreditCard,
  ChartColumnBig,
  Sparkles,
  Settings,
  Shield,
  Activity,
  CalendarRange,
  Plus,
} from 'lucide-react';

// Shared menu items configuration - clean root-level paths for subdomain
// Uses translation keys that will be resolved with t() function.
// Produce Planner is dev-only: its sidebar entry is gated by the build-time flag
// (see featureFlags.js) so it never appears in the prod app.
const MENU_ITEMS = [
  { nameKey: 'nav.speakWithBiq', icon: MessageSquare, path: '/biq' },
  { nameKey: 'nav.map', icon: GlobalProduceMap, path: '/map' },
  ...(ENABLE_PROCUREMENT_PLANNER
    ? [{ nameKey: 'nav.procurement', icon: CalendarRange, path: ROUTES.PROCUREMENT }]
    : []),
  { nameKey: 'nav.bloomTerminal', icon: ChartColumnBig, path: '/analytics' },
  { nameKey: 'nav.alerts', icon: NavBell, path: '/alerts' },
  {
    nameKey: 'nav.settings',
    icon: Settings,
    path: '/settings',
    hasDropdown: true,
    dropdownItems: [
      { nameKey: 'nav.account', path: '/account', icon: User },
      { nameKey: 'nav.usage', path: '/usage', icon: Activity },
      { nameKey: 'nav.billing', path: '/billing', icon: CreditCard },
      { nameKey: 'nav.privacy', path: '/privacy', icon: Shield },
    ]
  },
  { nameKey: 'nav.upgrade', icon: Sparkles, path: '/subscription' },
];

// Paths that render the same page as a nav item but do not equal its `path`.
// Root is the post-login landing route and renders BiQ (App.jsx `<Route index>`);
// '/chatbot' is a legacy alias. Without these, landing on or refreshing
// app.bloomiq.io left every sidebar item unselected.
const NAV_PATH_ALIASES = {
  '/biq': ['/', '/chatbot'],
};

// Every path that renders the BiQ chat page — drives both the nav selection
// and the BiQ-only sidebar sections (New chat + chat history).
const BIQ_PATHS = ['/biq', ...NAV_PATH_ALIASES['/biq']];

const COLLAPSED_SIDEBAR_WIDTH = 89;
const SIDEBAR_EXPANDED_WIDTH = 280;
const SIDEBAR_CONTENT_GAP = 0;
const LOGO_WIDTH = 25;
const LOGO_HEIGHT = 34;
const MENU_ICON_SIZE = 20;
const MENU_ITEM_SIZE = 30;
const MENU_ITEM_GAP_COLLAPSED = 15;
const TOGGLE_ICON_SIZE = 24;

const menuIconWrapClass =
  'flex shrink-0 items-center justify-center p-[5px]';

// Nav labels wrap to a second line rather than ellipsize. The expanded sidebar
// is a fixed 280px that does not scale, leaving ~177px for the label — enough
// for every current translation at the default font size, but not once the
// letter-size control scales the text, where "Global Produce Map" and "Produce
// Planner" were being cut to "Global Produ…".
//
// Rows are therefore `min-h-[40px]` with their own padding, not a fixed 40px:
// they had no gap between them, so a reflowed label in a fixed row spilled over
// its neighbours instead of growing it. Height is identical at the default size
// (a one-line label sits well under the 40px floor) and only grows when the
// label genuinely needs a second line. `title` still exposes the full text.
// Left pad is untouched so the icon column does not shift.
const getMenuItemClassName = (isCollapsed, active) => {
  const layoutClass = isCollapsed
    ? 'mx-auto w-[30px] justify-center'
    : 'w-full pl-[17.25px] pr-[6px] gap-[10px]';
  const sizeClass = isCollapsed ? 'h-[30px]' : 'min-h-[40px] py-[5px]';
  const stateClass = active
    ? isCollapsed
      ? 'bg-[#E3F5EB] dark:bg-[#173322]'
      : 'bg-[#12823F] text-white'
    : 'hover:bg-[#E8EDEA] dark:hover:bg-[#282a29]/50';

  return `flex ${sizeClass} items-center rounded-[8px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/60 ${layoutClass} ${stateClass}`;
};

const getMenuItemTextClassName = (active, isCollapsed) => {
  if (active) {
    if (isCollapsed) {
      return 'text-[#444746] dark:text-[#7EE6A4]';
    }
    return 'text-white';
  }
  return 'text-[#444746] dark:text-[#f0f5f2]';
};

const SidebarHeader = ({
  isCollapsed,
  showSidebarToggle,
  onSidebarToggle,
  onMenuItemClick,
}) => {
  const { t } = useTranslation();
  // Keep hit area ≥ logo size so logo↔icon swap does not flicker at the edges.
  const collapsedToggleSize = Math.max(TOGGLE_ICON_SIZE, LOGO_WIDTH, LOGO_HEIGHT);

  if (isCollapsed && showSidebarToggle && onSidebarToggle) {
    return (
      <div className="flex items-center justify-center mb-[24px]">
        <HoverTooltip label={t('nav.openSidebar')}>
          <button
            type="button"
            onClick={onSidebarToggle}
            className="group relative flex cursor-pointer items-center justify-center"
            style={{ width: `${collapsedToggleSize}px`, height: `${collapsedToggleSize}px` }}
            aria-label={t('nav.openSidebar')}
          >
            <img
              src="/assets/logo.png"
              alt="Analytics"
              className="pointer-events-none absolute object-contain opacity-100 group-hover:opacity-0"
              style={{ width: `${LOGO_WIDTH}px`, height: `${LOGO_HEIGHT}px` }}
            />
            <SideNavExpand
              size={TOGGLE_ICON_SIZE}
              className="pointer-events-none absolute text-[#444746] opacity-0 group-hover:opacity-100 dark:text-[#f0f5f2]"
            />
          </button>
        </HoverTooltip>
      </div>
    );
  }

  return (
    // pr-10 lg:pr-0: keeps the logo clear of the mobile drawer's floating
    // absolute close button (top-[16px] right-[16px]) below lg only; desktop
    // (which renders its own in-flow sidebar-toggle button on this row) is
    // restored to its original spacing via lg:pr-0.
    <div className="flex items-center justify-between mb-[24px] pr-10 lg:pr-0">
      <Link
        to="/biq"
        onClick={() => onMenuItemClick?.('/biq')}
      >
        <img
          src="/assets/logo.png"
          alt="Analytics"
          className="object-contain"
          style={{ width: `${LOGO_WIDTH}px`, height: `${LOGO_HEIGHT}px` }}
        />
      </Link>
      {showSidebarToggle && onSidebarToggle && (
        <HoverTooltip label={t('nav.closeSidebar')}>
          <button
            type="button"
            onClick={onSidebarToggle}
            className="group relative flex cursor-pointer items-center justify-center"
            style={{ width: `${TOGGLE_ICON_SIZE}px`, height: `${TOGGLE_ICON_SIZE}px` }}
            aria-label={t('nav.closeSidebar')}
          >
            <SideNav
              size={TOGGLE_ICON_SIZE}
              className="pointer-events-none absolute text-[#444746] opacity-100 group-hover:opacity-0 dark:text-[#f0f5f2]"
            />
            <SideNavCollapse
              size={TOGGLE_ICON_SIZE}
              className="pointer-events-none absolute text-[#444746] opacity-0 group-hover:opacity-100 dark:text-[#f0f5f2]"
            />
          </button>
        </HoverTooltip>
      )}
    </div>
  );
};

// Shared Menu Content Component
const MenuContent = ({
  isCollapsed = false,
  openDropdown,
  onDropdownToggle,
  onDropdownItemClick,
  onMenuItemClick,
  onSidebarToggle,
  showSidebarToggle = false,
  location,
  navigate
}) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  
  const isActive = (path) => {
    const { pathname } = location;
    if (pathname === path) return true;
    if (NAV_PATH_ALIASES[path]?.includes(pathname)) return true;
    // Keep the parent item selected on sub-pages (e.g. /alerts/configure,
    // /alerts/edit/:id). Guarded against '/' so root never matches everything.
    return path !== '/' && pathname.startsWith(`${path}/`);
  };

  const isDropdownActive = (item) => {
    if (!item.dropdownItems) return false;
    return item.dropdownItems.some(dropdownItem => isActive(dropdownItem.path));
  };

  const handleDropdownToggle = (itemNameKey) => {
    if (isCollapsed) {
      const item = MENU_ITEMS.find((menuItem) => menuItem.nameKey === itemNameKey);
      const firstPath = item?.dropdownItems?.[0]?.path;
      if (firstPath) {
        navigate(firstPath);
      }
      return;
    }
    onDropdownToggle(itemNameKey);
  };

  const handleDropdownItemClick = (path) => {
    navigate(path);
    onDropdownItemClick();
  };

  const handleMenuItemClick = () => {
    if (onMenuItemClick) {
      onMenuItemClick();
    }
  };

  return (
    <>
      <SidebarHeader
        isCollapsed={isCollapsed}
        showSidebarToggle={showSidebarToggle}
        onSidebarToggle={onSidebarToggle}
        onMenuItemClick={handleMenuItemClick}
      />

      {/* Navigation Menu */}
      <nav>
        <ul
          className="flex flex-col"
          style={isCollapsed ? { gap: MENU_ITEM_GAP_COLLAPSED } : undefined}
        >          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path) || isDropdownActive(item);
            const isDropdownOpen = openDropdown === item.nameKey;

            return (
              <li
                key={item.nameKey}
                className="relative"
                data-menu-item={item.nameKey}
              >
                {item.hasDropdown ? (
                  <>
                    <button
                      data-dropdown-button
                      onClick={() => handleDropdownToggle(item.nameKey)}
                      title={t(item.nameKey)}
                      className={`${getMenuItemClassName(isCollapsed, active)} text-left`}
                    >
                      <span
                        className={menuIconWrapClass}
                        style={{ width: MENU_ITEM_SIZE, height: MENU_ITEM_SIZE }}
                      >
                        <Icon
                          size={MENU_ICON_SIZE}
                          className={getMenuItemTextClassName(active, isCollapsed)}
                        />
                      </span>
                      {!isCollapsed && (
                        <>
                          <span className={`font-['Inter'] font-medium flex-1 min-w-0 break-words leading-snug ${getMenuItemTextClassName(active, isCollapsed)}`} style={{ fontSize }}>
                            {t(item.nameKey)}
                          </span>
                          <ChevronDown
                            size={20}
                            className={`flex-shrink-0 transition-transform ${getMenuItemTextClassName(active, isCollapsed)} ${
                              isDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </>
                      )}
                    </button>
                    {isDropdownOpen &&
                      !isCollapsed &&
                      item.dropdownItems && (
                        <div data-dropdown-menu className="mt-[4px]">
                          {item.dropdownItems.map((dropdownItem) => {
                            const dropdownActive = isActive(dropdownItem.path);
                            return (
                              <Link
                                key={dropdownItem.path}
                                to={dropdownItem.path}
                                onClick={() => handleDropdownItemClick(dropdownItem.path)}
                                className={`w-full flex items-center min-h-[40px] py-[5px] pl-[60px] pr-[20px] rounded-[8px] transition-colors ${
                                  dropdownActive
                                    ? "bg-[#E8EDEA] dark:bg-[#282a29]"
                                    : "hover:bg-[#E8EDEA] dark:hover:bg-[#282a29]/50"
                                }`}
                              >
                                <span
                                  className={`font-['Inter'] font-medium flex-1 min-w-0 break-words leading-snug ${
                                    dropdownActive
                                      ? "text-bloom-green"
                                      : "text-[#444746] dark:text-[#f0f5f2]"
                                  }`}
                                  style={{ fontSize }}
                                >
                                  {t(dropdownItem.nameKey)}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => handleMenuItemClick(item.path)}
                    title={t(item.nameKey)}
                    className={getMenuItemClassName(isCollapsed, active)}
                  >
                    <span
                      className={menuIconWrapClass}
                      style={{ width: MENU_ITEM_SIZE, height: MENU_ITEM_SIZE }}
                    >
                      <Icon
                        size={MENU_ICON_SIZE}
                        className={getMenuItemTextClassName(active, isCollapsed)}
                      />
                    </span>
                    {!isCollapsed && (
                      <span className={`font-['Inter'] font-medium flex-1 min-w-0 break-words leading-snug ${getMenuItemTextClassName(active, isCollapsed)}`} style={{ fontSize }}>
                        {t(item.nameKey)}
                      </span>
                    )}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
};

const SidebarNewChatButton = ({ isCollapsed, onClick }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();

  const collapsedClassName =
    [
      'cursor-pointer items-center justify-center rounded-[8px] border-0 bg-[#16A34A]',
      'shadow-[0_4px_12px_rgba(22,163,74,0.3)]',
      'transition-[box-shadow,background-color]',
      // Light mode hover
      'hover:bg-[#15803D] hover:shadow-[0_6px_16px_rgba(22,163,74,0.3)]',
      // Dark mode hover/active — white 20% overlay + green glow (Figma)
      'dark:hover:bg-[color-mix(in_srgb,white_20%,#16A34A)]',
      'dark:hover:shadow-[0_6px_16px_rgba(21,203,94,0.3),0_0_0_4px_rgba(31,180,87,0.18)]',
      'dark:active:bg-[color-mix(in_srgb,white_20%,#16A34A)]',
      'dark:active:shadow-[0_6px_16px_rgba(21,203,94,0.3),0_0_0_4px_rgba(31,180,87,0.18)]',
    ].join(' ');

  const expandedClassName =
    'cursor-pointer items-center justify-center rounded-[8px] border border-[#BBF7D0] bg-white shadow-none transition-[border-color,background-color] hover:border-[#15803D] hover:bg-[#DCFCE7] dark:border-[#16A34A] dark:bg-[#173322] dark:hover:border-[#15803D] dark:hover:bg-[#1E482F]';

  if (isCollapsed) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={t('nav.newChat')}
        className={`mx-auto flex h-[42px] w-[42px] ${collapsedClassName}`}
      >
        <Plus size={MENU_ICON_SIZE} className="flex-shrink-0 text-white" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-[42px] w-full gap-[10px] px-[20px] ${expandedClassName}`}
    >
      <Plus size={MENU_ICON_SIZE} className="flex-shrink-0 text-bloom-green" />
      <span className="font-['Inter'] font-medium text-bloom-green" style={{ fontSize }}>
        {t('nav.newChat')}
      </span>
    </button>
  );
};

const SidebarFooter = ({ isCollapsed }) => {
  return (
    <div className="mt-auto w-full">
      <div
        className={`space-y-[12px] ${
          isCollapsed
            ? ''
            : 'border-t border-[#C3C4C3] dark:border-[#5d625f] pt-[16px]'
        }`}
      >
        <LanguageSelector variant="sidebar" isCollapsed={isCollapsed} />
        <SidebarUserMenu isCollapsed={isCollapsed} />
      </div>
    </div>
  );
};

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const sidebarRef = useRef(null);
  const isBiQPage = BIQ_PATHS.includes(location.pathname);
  const { startNewChat } = useBiqChatHistory();

  const handleNewChat = () => {
    startNewChat();
    if (!isBiQPage) navigate('/biq');
  };

  const handleSidebarToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Close dropdown when sidebar collapses
  useEffect(() => {
    if (isCollapsed) {
      setOpenDropdown(null);
    }
  }, [isCollapsed]);

  const handleDropdownToggle = (itemNameKey) => {
    if (openDropdown === itemNameKey) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(itemNameKey);
    }
  };

  const handleDropdownItemClick = () => {
    // setOpenDropdown(null);
  };

  return (
    <div className="relative" ref={sidebarRef}>
      {/* Sidebar - Desktop only */}
      <aside
        className="hidden lg:block fixed top-0 left-0 h-full transition-all duration-300 ease-in-out z-30 bg-[#F0F4F1] dark:bg-[#141614] border-r border-[#E2E2E2] dark:border-[#282a29]"
        style={{ width: `${isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : SIDEBAR_EXPANDED_WIDTH}px` }}
      >
        <div className="flex h-full min-h-0 flex-col px-[20px] pt-[24px] pb-[40px]">
          {/* scrollbar-hide: the sidebar still scrolls, but the native bar is
              hidden — it read as a heavy dark column against the rail. */}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
            <MenuContent
              isCollapsed={isCollapsed}
              openDropdown={openDropdown}
              onDropdownToggle={handleDropdownToggle}
              onDropdownItemClick={handleDropdownItemClick}
              onSidebarToggle={handleSidebarToggle}
              showSidebarToggle
              location={location}
              navigate={navigate}
            />

            {isBiQPage && (
              <div
                className={
                  isCollapsed
                    ? 'mt-[16px] border-t border-[#C3C4C3] dark:border-[#5d625f] pt-[16px]'
                    : 'mt-[32px]'
                }
              >
                <SidebarNewChatButton isCollapsed={isCollapsed} onClick={handleNewChat} />
                {!isCollapsed && <SidebarChatHistory />}
              </div>
            )}
          </div>

          <div className="shrink-0">
            <SidebarFooter isCollapsed={isCollapsed} />
          </div>
        </div>
      </aside>
    </div>
  );
};

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const menuRef = useRef(null);
  const isBiQPage = BIQ_PATHS.includes(location.pathname);
  const { startNewChat } = useBiqChatHistory();

  const handleDropdownToggle = (itemNameKey) => {
    if (openDropdown === itemNameKey) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(itemNameKey);
    }
  };

  const handleDropdownItemClick = () => {
    setOpenDropdown(null);
    onClose();
  };

  const handleMenuItemClick = () => {
    onClose();
  };

  // Close sidebar on navigation
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location.pathname]);

  // Close dropdown when menu closes
  useEffect(() => {
    if (!isOpen) {
      setOpenDropdown(null);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      {/* Overlay backdrop.
          The drawer stack sits above every page-level layer: page filter bars
          go up to z-[1300] (map/home), the MapSidebar mobile bottom sheet is
          z-[2100], and the BiQ launcher + popup are z-[2000]/z-[2001] — so the
          drawer itself lives one tier higher (2200/2210), a true top-level
          modal above all of it. */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[2200] transition-opacity duration-300 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Mobile Menu */}
      <aside
        ref={menuRef}
        className={`fixed top-0 left-0 h-full w-[calc(100vw-32px)] max-w-[354px] bg-[#F0F4F1] dark:bg-[#141614] border-r border-[#E2E2E2] dark:border-[#282a29] transition-transform duration-300 ease-in-out z-[2210] lg:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="relative flex h-full min-h-0 flex-col px-[20px] pt-[24px] pb-[40px]">
          {/* Close Button — floated top-right so the logo aligns to the top
              instead of being pushed down by the X's own row. */}
          <button
            onClick={onClose}
            className="absolute top-[16px] right-[16px] z-10 flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[8px] bg-[#E8EDEA] transition-colors hover:bg-[#DCE1DE] dark:bg-[#282a29] dark:hover:bg-[#282a29]/80"
            aria-label="Close menu"
          >
            <X size={20} className="text-[#444746] dark:text-[#f0f5f2]" />
          </button>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
            <MenuContent
              isCollapsed={false}
              openDropdown={openDropdown}
              onDropdownToggle={handleDropdownToggle}
              onDropdownItemClick={handleDropdownItemClick}
              onMenuItemClick={handleMenuItemClick}
              location={location}
              navigate={navigate}
            />

            {isBiQPage && (
              <div className="mt-[32px]">
                <SidebarNewChatButton
                  isCollapsed={false}
                  onClick={() => {
                    startNewChat();
                    if (!isBiQPage) navigate('/biq');
                    onClose();
                  }}
                />
                <SidebarChatHistory onSelectChat={onClose} />
              </div>
            )}
          </div>

          <div className="shrink-0">
            <SidebarFooter isCollapsed={false} />
          </div>
        </div>
      </aside>
    </>
  );
};

// BiQ launcher drag: persisted position is normalised {u,v} in [0,1] over the legal
// travel range, so it survives viewport/breakpoint changes without going off-screen.
const BIQ_LAUNCHER_POS_KEY = 'biq.launcher.pos.v1';
const BIQ_LAUNCHER_SIZE = 60;

const biqLauncherTravel = (bounds) => ({
  minX: bounds.left,
  maxX: Math.max(bounds.left, bounds.right - BIQ_LAUNCHER_SIZE),
  minY: bounds.top,
  maxY: Math.max(bounds.top, bounds.bottom - BIQ_LAUNCHER_SIZE),
});

const readBiqLauncherPos = () => {
  try {
    const raw = window.localStorage.getItem(BIQ_LAUNCHER_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const { u, v } = parsed || {};
    if (!Number.isFinite(u) || !Number.isFinite(v) || u < 0 || u > 1 || v < 0 || v > 1) return null;
    return { u, v };
  } catch {
    return null;
  }
};

const writeBiqLauncherPos = (u, v) => {
  try {
    window.localStorage.setItem(BIQ_LAUNCHER_POS_KEY, JSON.stringify({ u, v }));
  } catch {
    // Private mode / storage quota — position just won't persist across reloads.
  }
};

// BiQ Chat Button Icon
const BiQChatIcon = ({ size = 25, color = 'white' }) => (
  <svg width={size} height={size} viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.75 16.25C23.75 16.913 23.4866 17.5489 23.0178 18.0178C22.5489 18.4866 21.913 18.75 21.25 18.75H6.25L1.25 23.75V3.75C1.25 3.08696 1.51339 2.45107 1.98223 1.98223C2.45107 1.51339 3.08696 1.25 3.75 1.25H21.25C21.913 1.25 22.5489 1.51339 23.0178 1.98223C23.4866 2.45107 23.75 3.08696 23.75 3.75V16.25Z" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { commodities } = useCommodities();
  const knownCommodities = useMemo(() => Object.values(commodities).flat(), [commodities]);
  const mapParams = useMapParamsState(location, navigate, knownCommodities);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false);
  const [isBiQChatOpen, setIsBiQChatOpen] = useState(false);
  const [isBiQLauncherVisible, setIsBiQLauncherVisible] = useState(true);
  // Lets a page open the chat from its own UI — the Map's shipment "Ask BiQ"
  // button. The page's subject is carried by BiqPageContext's extras, which
  // BiQChatPopup already reads, so this only has to open the popup.
  const handleAskBiq = useCallback(() => {
    trackEvent(EVENTS.BIQ_POPUP_OPENED);
    setIsBiQChatOpen(true);
  }, []);
  const biQButtonRef = useRef(null);
  // Launcher drag: x/y are the box's absolute px position from the viewport's top-left
  // (constant 60x60 footprint regardless of the open/closed 60->30 visual shrink).
  const biqLauncherX = useMotionValue(0);
  const biqLauncherY = useMotionValue(0);
  const biqDragControls = useDragControls();
  const biqDragSuppressClickRef = useRef(false);
  const biqDragSessionRef = useRef(null);
  const biqLauncherBounds = useViewportBounds(isTabletOrMobile ? 16 : 32);

  // Drag-visual toggle as a DOM attribute (not React state) — flips synchronously on
  // every pointerdown/up with no re-render, so it can't add a frame of lag to the drag.
  const setLauncherDragging = (on) => {
    const el = biQButtonRef.current;
    if (!el) return;
    if (on) el.setAttribute('data-dragging', '');
    else el.removeAttribute('data-dragging');
  };

  // Restore the launcher's persisted position (or default to the bottom-right slot)
  // whenever the legal travel range changes — never writes storage from this effect,
  // and never fights an in-progress drag (bounds can change mid-drag, e.g. a resize).
  useEffect(() => {
    if (!biqLauncherBounds.width || !biqLauncherBounds.height) return;
    if (biqDragSessionRef.current?.moved) return;
    const { minX, maxX, minY, maxY } = biqLauncherTravel(biqLauncherBounds);
    const stored = readBiqLauncherPos();
    if (stored) {
      biqLauncherX.set(minX + stored.u * (maxX - minX));
      biqLauncherY.set(minY + stored.v * (maxY - minY));
    } else {
      biqLauncherX.set(maxX);
      biqLauncherY.set(maxY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biqLauncherBounds.left, biqLauncherBounds.top, biqLauncherBounds.right, biqLauncherBounds.bottom]);

  // Clamp into the current travel range and persist as a normalised {u,v} — called only
  // on a deliberate drag end, never by a resize/bounds-change reflow.
  const commitBiqLauncherPosition = () => {
    const { minX, maxX, minY, maxY } = biqLauncherTravel(biqLauncherBounds);
    const clampedX = Math.min(Math.max(biqLauncherX.get(), minX), maxX);
    const clampedY = Math.min(Math.max(biqLauncherY.get(), minY), maxY);
    biqLauncherX.set(clampedX);
    biqLauncherY.set(clampedY);
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    writeBiqLauncherPos((clampedX - minX) / rangeX, (clampedY - minY) / rangeY);
    setLauncherDragging(false);
    biqDragSessionRef.current = null;
  };

  const isBiQPage = BIQ_PATHS.includes(location.pathname);
  const isHomePage = location.pathname === '/overview';
  const isTerminalPage = location.pathname === '/analytics';
  // Map page renders its own inline hamburger (same row as its filter strip,
  // on the gradient page background) instead of the standalone mobile
  // <header> below — keeps the header from double-rendering a menu button.
  const isMapPage = mapParams.active;
  // Produce Planner and Looker Studio also render their own inline mobile
  // top row (hamburger + fullscreen, same pattern as Map/Analytics) — same
  // reasoning as isMapPage/isTerminalPage above, extended to these routes so
  // they don't double-render a menu button under their own top row.
  const pageProvidesOwnMenu =
    isMapPage ||
    isTerminalPage ||
    location.pathname === ROUTES.PROCUREMENT ||
    location.pathname === '/lookerstudio';
  const mainRef = useRef(null);

  // `?fullscreen=1` is honored on /analytics, /map, and — when enabled —
  // /produce-planner (the Produce Planner). These views share the same
  // chrome-hiding treatment: no app sidebar, no top bar, content fills the
  // viewport.
  const isFullscreenMode = useMemo(() => {
    const fullscreenPaths = ['/analytics', '/map',
      ...(ENABLE_PROCUREMENT_PLANNER ? [ROUTES.PROCUREMENT] : [])];
    if (!fullscreenPaths.includes(location.pathname) && !mapParams.active) return false;
    // /map keeps its flags in state (no query string); other routes still read the URL.
    const params = mapParams.active ? mapParams.params : new URLSearchParams(location.search);
    return params.get('fullscreen') === '1';
  }, [location.pathname, location.search, mapParams.params]);

  // /map?sidebarCollapsed=1 deep-link (e.g. arriving from an Analytics scorecard)
  // requests the same forced-collapse treatment as fullscreen.
  const isMapDeepLinkCollapsed = useMemo(() => {
    if (!mapParams.active) return false;
    return mapParams.params.get('sidebarCollapsed') === '1';
  }, [mapParams.active, mapParams.params]);

  const shouldForceCollapse = isFullscreenMode || isMapDeepLinkCollapsed;

  // When entering a force-collapse view, remember the prior expanded state and
  // collapse; restore on exit. Single ref pair handles both triggers since they
  // share the same restore semantics.
  const prevSidebarCollapsedRef = useRef(false);
  const didForceCollapseRef = useRef(false);

  useEffect(() => {
    if (isTabletOrMobile) return;

    if (shouldForceCollapse) {
      if (!didForceCollapseRef.current) {
        prevSidebarCollapsedRef.current = sidebarCollapsed;
        didForceCollapseRef.current = true;
      }
      setSidebarCollapsed(true);
    } else if (didForceCollapseRef.current) {
      // Restore expanded by default (or previous state) after exiting force-collapse
      setSidebarCollapsed(prevSidebarCollapsedRef.current || false);
      didForceCollapseRef.current = false;
    }
  }, [isTabletOrMobile, shouldForceCollapse, sidebarCollapsed]);

  // Detect if we're on tablet/mobile (< 1024px)
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 1024;
      setIsTabletOrMobile(isMobile);
      if (!isMobile) {
        setIsMobileSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Custom terminal scrollbar is desktop-only chrome: skip it in fullscreen and
  // on tablet/mobile, where its fixed-width column is too heavy for the viewport.
  const showTerminalScrollbar = !isFullscreenMode && !isTabletOrMobile;

  // Where the content column starts. BiQ paints its background as a viewport-
  // wide fixed layer (it has to sit behind the mobile top bar too), so it can't
  // see this offset on its own — publish it as a CSS var it can inset its
  // gradient by, so the welcome glow stays centered on the content column at
  // any screen size / collapse state instead of on the whole viewport.
  const contentLeftOffset =
    isTabletOrMobile || isFullscreenMode
      ? 0
      : (sidebarCollapsed ? COLLAPSED_SIDEBAR_WIDTH : SIDEBAR_EXPANDED_WIDTH) +
        SIDEBAR_CONTENT_GAP;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--content-left', `${contentLeftOffset}px`);
    return () => root.style.removeProperty('--content-left');
  }, [contentLeftOffset]);

  const handleMobileMenuToggle = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileSidebarOpen(false);
  };

  return (
    <BiqLiveChatsProvider>
    <BiqChatHistoryProvider>
    <BiqPageContextProvider searchOverride={mapParams.active ? mapParams.params.toString() : undefined}>
    <MapParamsContext.Provider value={mapParams}>
    <div className={`app-shell-h flex overflow-hidden ${isBiQPage ? '' : 'bg-[#F9F9F9] bg-dark-app'}`}>
      {/* Desktop Sidebar (hidden entirely in fullscreen mode — Analytics or Map) */}
      {!isTabletOrMobile && !isFullscreenMode && (
        <Sidebar
          isCollapsed={sidebarCollapsed}
          setIsCollapsed={setSidebarCollapsed}
        />
      )}

      {/* Mobile/Tablet Menu */}
      <MobileMenu
        isOpen={isTabletOrMobile ? isMobileSidebarOpen : false}
        onClose={handleMobileMenuClose}
      />

      <div
        className={`flex-1 flex flex-col h-full transition-all duration-300 min-w-0 ${isTabletOrMobile ? 'ml-0' : ''}`}
        style={
          !isTabletOrMobile ? { marginLeft: `${contentLeftOffset}px` } : undefined
        }
      >
        {!isFullscreenMode && isTabletOrMobile && !pageProvidesOwnMenu && (
          <header className={`shrink-0 py-2 px-4 flex items-center lg:hidden ${isBiQPage ? '' : 'bg-[#F9F9F9] bg-dark-app'}`}>
            <MobileMenuButton onClick={handleMobileMenuToggle} />
          </header>
        )}

        {isTerminalPage ? (
          <div className="flex-1 flex min-h-0 min-w-0 mr-0">
            {/* The custom terminal scrollbar is a 24px column with 32px arrow
                buttons — desktop chrome. On tablet/mobile that column eats real
                width, so there we hide it and let the main area use the thin
                native scrollbar every other page uses (see index.css). */}
            <main
              ref={mainRef}
              id="bloom-terminal-main"
              className={`flex-1 overflow-y-auto min-h-0 min-w-0 bg-[#F9F9F9] bg-dark-app ${
                showTerminalScrollbar ? 'scrollbar-hide' : ''
              }`}
            >
              <Outlet context={{ onMenuClick: handleMobileMenuToggle, onAskBiq: handleAskBiq }} />
            </main>
            {showTerminalScrollbar && (
              <CustomScrollbar scrollContainerRef={mainRef} />
            )}
          </div>
        ) : (
          <main
            className={
              // `min-w-0`: a flex item defaults to `min-width: auto`, i.e. its
              // max-content width, so one over-wide descendant stretches this
              // whole column past the viewport and the page pans sideways. The
              // terminal <main> above already carries it.
              'flex-1 overflow-y-auto min-w-0 ' +
              (isHomePage || isBiQPage
                ? ''
                : 'bg-[#F9F9F9] bg-dark-app')
            }
          >
            {/* Outlet context: lets a page render its own inline mobile
                hamburger (e.g. MapPage, next to its filter strip) wired to
                the same drawer toggle the standalone header above uses. */}
            <Outlet context={{ onMenuClick: handleMobileMenuToggle, onAskBiq: handleAskBiq }} />
          </main>
        )}
      </div>

      {/* BiQ Chat Popup */}
      {!isBiQPage && isBiQLauncherVisible && (
        <BiQChatPopup
          isOpen={isBiQChatOpen}
          onClose={() => {
            trackEvent(EVENTS.BIQ_POPUP_CLOSED);
            setIsBiQChatOpen(false);
          }}
          buttonRef={biQButtonRef}
          launcherX={biqLauncherX}
          launcherY={biqLauncherY}
          bounds={biqLauncherBounds}
        />
      )}

      {/* Floating BiQ Chat Button — a draggable 60x60 box; the visual 60->30 shrink
          happens on the button inside, so the box's drag geometry never changes. */}
      {!isBiQPage && isBiQLauncherVisible && (
        <motion.div
          className="group fixed left-0 top-0 z-[2000] h-[60px] w-[60px]"
          style={{ x: biqLauncherX, y: biqLauncherY, willChange: 'transform' }}
          drag
          dragControls={biqDragControls}
          dragListener={false}
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={{
            left: biqLauncherBounds.left,
            top: biqLauncherBounds.top,
            right: Math.max(biqLauncherBounds.left, biqLauncherBounds.right - BIQ_LAUNCHER_SIZE),
            bottom: Math.max(biqLauncherBounds.top, biqLauncherBounds.bottom - BIQ_LAUNCHER_SIZE),
          }}
          onDragStart={() => {
            biqDragSessionRef.current = { moved: true };
            biqDragSuppressClickRef.current = true;
            setLauncherDragging(true);
          }}
          onDragEnd={commitBiqLauncherPosition}
        >
          <button
            type="button"
            onClick={(event) => {
              // A latched suppression only eats a pointer-generated click (event.detail > 0
              // for mouse/touch); Enter/Space activation always has detail === 0 and toggles.
              if (biqDragSuppressClickRef.current) {
                biqDragSuppressClickRef.current = false;
                if (event.detail > 0) return;
              }
              const newState = !isBiQChatOpen;
              trackEvent(newState ? EVENTS.BIQ_POPUP_OPENED : EVENTS.BIQ_POPUP_CLOSED);
              setIsBiQChatOpen(newState);
            }}
            onPointerDown={(e) => {
              if (e.button !== 0 || !e.isPrimary) return;
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch {
                // Capture can fail (e.g. already released) — the drag still works without it.
              }
              biqDragSuppressClickRef.current = false;
              biqDragSessionRef.current = { moved: false };
              biqDragControls.start(e, { distanceThreshold: e.pointerType === 'touch' ? 6 : 2 });
            }}
            // Don't clear the click suppression here: on touch, capture is released
            // *before* the click fires, so clearing would let a drag release toggle.
            // The next pointerdown resets it, and keyboard clicks bypass it (detail 0).
            onPointerCancel={() => setLauncherDragging(false)}
            onLostPointerCapture={() => setLauncherDragging(false)}
            ref={biQButtonRef}
            style={{ touchAction: 'none', userSelect: 'none', WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent' }}
            draggable={false}
            className={`absolute inset-0 m-auto flex items-center justify-center bg-[#16A34A] cursor-pointer shadow-[0_8px_24px_rgba(22,163,74,0.35)] transition-[width,height,border-radius,scale,box-shadow,background-color] duration-200 ease-out motion-reduce:transition-none not-data-dragging:hover:bg-[#15803D] not-data-dragging:hover:scale-105 motion-reduce:hover:scale-100 data-dragging:cursor-grabbing data-dragging:scale-[1.06] data-dragging:shadow-[0_14px_32px_rgba(22,163,74,0.45)] data-dragging:duration-100 motion-reduce:data-dragging:scale-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#16A34A] ${
              isBiQChatOpen
                // Shrunk while open: the panel is now the focus, and an invisible 60px
                // hit area (via `after`, matching the closed footprint) keeps the button
                // easy to re-tap. Tailwind v4's `after:` variant injects `content: ''`.
                ? 'w-[30px] h-[30px] rounded-[8px] ring-2 ring-[#16A34A]/40 after:absolute after:-inset-[15px]'
                : 'w-[60px] h-[60px] rounded-[16px]'
            }`}
            aria-expanded={isBiQChatOpen}
            aria-controls="biq-panel"
            aria-label={isBiQChatOpen ? 'Close BiQ' : 'Speak with BiQ'}
          >
            {/* Two stacked icons cross-fade/rotate via Tailwind's individual scale/rotate
                props — a single `transition-[transform,...]` would not animate them. */}
            <span className="relative block h-[28px] w-[28px]">
              <span aria-hidden="true" className={`absolute inset-0 flex items-center justify-center transition-[opacity,scale,rotate] duration-200 ease-[cubic-bezier(.2,0,0,1)] motion-reduce:transition-none ${isBiQChatOpen ? 'scale-50 opacity-0 rotate-90' : 'scale-100 opacity-100 rotate-0'}`}>
                <BiQChatIcon />
              </span>
              <span aria-hidden="true" className={`absolute inset-0 flex items-center justify-center transition-[opacity,scale,rotate] duration-200 ease-[cubic-bezier(.2,0,0,1)] motion-reduce:transition-none ${isBiQChatOpen ? 'scale-100 opacity-100 rotate-0' : 'scale-50 opacity-0 -rotate-90'}`}>
                <ChevronDown size={18} strokeWidth={2.5} className="text-white" />
              </span>
            </span>
          </button>

          {/* Same badge as before; only difference is it's not rendered while the panel is
              open (the launcher is 30px then). It is not a drag handle — only the launcher
              button starts a drag session. */}
          {!isBiQChatOpen && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isBiQChatOpen) {
                  trackEvent(EVENTS.BIQ_POPUP_CLOSED);
                }
                setIsBiQChatOpen(false);
                setIsBiQLauncherVisible(false);
              }}
              className="absolute -top-[6px] -right-[6px] w-[26px] h-[26px] rounded-full bg-white hover:bg-[#F0F5F2] dark:bg-[#282a29] dark:hover:bg-[#3a3d3c] shadow-[0_2px_6px_rgba(0,0,0,0.15)] flex items-center justify-center transition-colors"
              aria-label="Hide BiQ"
            >
              <X size={16} className="text-[#444746] dark:text-[#f0f5f2]" />
            </button>
          )}
        </motion.div>
      )}

      {/* Onboarding tour — mounted on every dashboard route, since first login
          lands on BiQ (`/`) rather than Overview. It renders nothing once the
          user has skipped or finished it. */}
      <TutorialModal />
    </div>
    </MapParamsContext.Provider>
    </BiqPageContextProvider>
    </BiqChatHistoryProvider>
    </BiqLiveChatsProvider>
  );
};

export default DashboardLayout;
